import React, { useEffect, useState } from 'react';
import './AdminUsers.css';

const formatName = (user) => [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unnamed user';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState('');

  const fetchUsers = async (search = query) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/admin/users?q=${encodeURIComponent(search)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to load users.');
      }

      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    fetchUsers(query);
  };

  const updateRole = async (userId, role) => {
    setUpdatingId(userId);
    setError('');

    try {
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({ role })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to update role.');
      }

      setUsers(current => current.map(user => user._id === userId ? data.user : user));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId('');
    }
  };

  return (
    <main className="admin-users-page">
      <section className="admin-users-shell">
        <div className="admin-users-header">
          <p>Admin</p>
          <h1>User Roles</h1>
          <span>Search users and manage who can access admin tools.</span>
        </div>

        <form className="admin-user-search" onSubmit={handleSearchSubmit}>
          <input
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search by name or email"
          />
          <button type="submit" className="next-btn">Search</button>
        </form>

        {error && <div className="admin-users-error">{error}</div>}
        {loading && <div className="admin-users-empty">Loading users...</div>}

        {!loading && (
          <div className="admin-users-table-wrap">
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user._id}>
                    <td>{formatName(user)}</td>
                    <td>{user.email}</td>
                    <td><span className={`role-badge role-${user.role}`}>{user.role}</span></td>
                    <td>
                      {user.role === 'admin' ? (
                        <button
                          type="button"
                          className="secondary-btn"
                          disabled={updatingId === user._id}
                          onClick={() => updateRole(user._id, 'user')}
                        >
                          Make user
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="next-btn"
                          disabled={updatingId === user._id}
                          onClick={() => updateRole(user._id, 'admin')}
                        >
                          Make admin
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!users.length && <div className="admin-users-empty">No users found.</div>}
          </div>
        )}
      </section>
    </main>
  );
};

export default AdminUsers;
